<template>
  <div id="contract-editor">
    <h1>
      <span>{{ $t(liquidization ? 'CONTRACT_LIQUIDIZATION' : 'CONTRACT_REPAYMENT') }}</span>
      &nbsp;
      <span v-if="contractId">{{ `#${contractId}` }}</span>
    </h1>

    <div v-show="step === 0">
      <repayment-info :contract="contract" ref="repaymentInfoRef" @transactions='emitTransactions'  @input="loadRepaymentInfo" :liquidization="liquidization" />

      <div class="controls">
        <primary-button :text="$t('BACK')" blue class="prev" @click="back()" />
        <primary-button :text="$t('NEXT')" class="next" @click="showPreview()" />
      </div>
    </div>

    <div v-show="step === 1">
      <preview
        :contract="contract"
        :client="client"
        :asset="asset"
        :liquidization="liquidization"
        :storageFeeLate="storageFeeLate"
        :overdueFee="overdueFee"
        :liquidizationFee="liquidizationFee"
        :failed-commitment-fee="failedCommitmentFee"
        :repayment-date="repaymentDate"
        :allTransactions="allTransactions.value"
        ref="previewRef"
      />

      <div class=" controls preview-controls">
        <center>{{ $t('PLEASE_CHECK_CONTRACT_INFO_BEFORE_PRINT') }}</center>
        <div class="buttons d-flex justify-content-center">
          <primary-button v-if="substep === 0" :text="$t('BACK')" blue class="prev" @click="showInputs()" />
          <primary-button :text="$t((substep === 1) ? 'PRINT_RECEIPT' : 'SAVE_AND_PRINT_RECEIPT')" :blue="substep === 1" :class="{ next: substep === 1, prev: substep === 0 }" @click="saveContractAndPrint()">
            <template slot="icon">
              <font-awesome-icon :icon="faPrint" />
            </template>
          </primary-button>
          <primary-button v-if="substep === 1" :text="$t('DONE')" class="next" @click="goToContractsDetail()" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from '@vue/composition-api';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faPrint } from '@fortawesome/free-solid-svg-icons';
import { createNamespacedHelpers } from 'vuex-composition-helpers';

import { PATHS, TRANSACTION_TYPES, TRANSACTION_ACTIONS, CONTRACT_STATUSES } from '@/consts';
import { applyParams } from '@/helpers';

import PrimaryButton from '@/components/PrimaryButton';

import RepaymentInfo from './RepaymentInfo';
import Preview from './Preview';
import ContractRepaymentService from './ContractRepaymentService';

const { useActions: useFlashActions } = createNamespacedHelpers('flash');

export default {
  name: 'ContractEditor',
  components: {
    RepaymentInfo,
    Preview,
    PrimaryButton,
    FontAwesomeIcon
  },
  metaInfo() {
    const { $i18n } = this;

    const title = $i18n.t('CONTRACT_REPAYMENT');

    return {
      title
    };
  },
  props: {
    liquidization: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { root }) {
    const { info, error } = useFlashActions(['info', 'error']);

    const step = ref(0);
    const substep = ref(0);

    const client = ref({});
    const asset = ref({
      meta: {}
    });
    const contract = ref({});

    const repaymentInfoRef = ref(null);
    const previewRef = ref(null);

    const savingContract = ref(false);

    const contractId = computed(() => root.$route.params.id);

    const repaymentDate = ref(null);
    const overdueFee = ref(0);
    const overridedOverdueFee = ref(null);
    const liquidizationFee = ref(0);
    const failedCommitmentFee = ref(0);
    const transactions = ref([]);
    const storageFeeLate = ref(0);
    const allTransactions = ref([]);

    const emitTransactions = (value) => {
      allTransactions.value = value;
    };

    onMounted(async () => {
      if (contractId.value) {
        const c = await ContractRepaymentService.getContractDetail(contractId.value);

        if (
          (c.status !== CONTRACT_STATUSES.DISBURSED)
          && (c.status !== CONTRACT_STATUSES.OVERDUE)
        ) {
          // root.$router.push(applyParams(PATHS.CONTRACT_DETAIL, c.publicId));
        }

        contract.value = c;
        asset.value = await ContractRepaymentService.getAsset(contract.value.assetId);
        client.value = await ContractRepaymentService.getClientById(contract.value.clientId);
      } else {
        root.$router.push(PATHS.CONTRACTS);
      }
    });

    const back = () => {
      root.$router.go(-1);
    };

    const showPreview = () => {
      const valid = repaymentInfoRef.value.validate();

      if (valid) {
        step.value = 1;
        substep.value = 0;
      }
    };

    const showInputs = () => {
      step.value = 0;
      substep.value = 0;
    };

    const saveContractAndPrint = async () => {
      if (savingContract.value) return;
      savingContract.value = true;

      try {
        if (substep.value === 0) {
          if (overridedOverdueFee.value || overridedOverdueFee.value === 0) {
            await ContractRepaymentService.overrideOverdueFee(contract.value.id, overridedOverdueFee.value);
          }

          const overdue = overdueFee.value - contract.value.chargedOverdueFee;
          if (overdue > 0) {
            await ContractRepaymentService.chargeOverdueFee(contract.value.id, {
              registeredOn: repaymentDate.value,
              amount: overdue
            });
          }

          if (liquidizationFee.value > 0) {
            await ContractRepaymentService.chargeLiquidizationFee(contract.value.id, {
              registeredOn: repaymentDate.value,
              amount: liquidizationFee.value
            });
          }

          if (failedCommitmentFee.value > 0) {
            await ContractRepaymentService.chargeFailedCommitmentFee(contract.value.id, {
              registeredOn: repaymentDate.value,
              amount: failedCommitmentFee.value
            });
          }

          await transactions.value.reduce(async (promise, t) => {
            await promise;
            return ContractRepaymentService.addTransaction(contract.value.id, {
              ...t,
              action: TRANSACTION_ACTIONS.REPAY,
              actionOn: repaymentDate.value
            });
          }, Promise.resolve());

          await ContractRepaymentService.repay(contractId.value, {
            repaidOn: repaymentDate.value,
          });

          info(root.$t('CONTRACT_REPAYMENT_SUCCEEDED'));
          ContractRepaymentService.addContractNotes(contract.value.id, props.liquidization ? 'Thanh lý hợp đồng' : 'Tất toán hợp đồng');

          substep.value = 1;
        }

        savingContract.value = false;

        setTimeout(() => {
          previewRef.value.print();
        }, 0);
      } catch (err) {
        savingContract.value = false;
        // TODO: prompt correct detailed error message based on error code
        const { message } = err;
        error(message);
      }
    };

    const goToContractsDetail = () => {
      root.$router.push(applyParams(PATHS.CONTRACT_DETAIL, contract.value.publicId));
    };

    const loadRepaymentInfo = (obj) => {
      repaymentDate.value = obj.transactions.filter((item) => item.actions !== 'WAIVE' && item.status !== 'RETRACTED' && !item.isRenewalDifference)[0].registeredOn;
      overdueFee.value = obj.overdueFee;
      overridedOverdueFee.value = obj.overridedOverdueFee;
      liquidizationFee.value = obj.liquidizationFee;
      failedCommitmentFee.value = obj.failedCommitmentFee;
      storageFeeLate.value = obj.storageFeeLate;

      transactions.value = obj.transactions.filter((t) => (!t.status && !!t.amount)).sort((a, b) => {
        if (a.registeredOn < b.registeredOn) {
          return -1;
        }

        if (a.registeredOn > b.registeredOn) {
          return 1;
        }

        if (
          (a.type === TRANSACTION_TYPES.CASH)
          && (b.type === TRANSACTION_TYPES.BANK)
        ) {
          return -1;
        }

        if (
          (b.type === TRANSACTION_TYPES.CASH)
          && (a.type === TRANSACTION_TYPES.BANK)
        ) {
          return 1;
        }

        return 0;
      });
    };

    return {
      step,
      substep,
      client,
      asset,
      contract,
      showPreview,
      showInputs,
      repaymentInfoRef,
      saveContractAndPrint,
      faPrint,
      previewRef,
      goToContractsDetail,
      contractId,
      back,
      loadRepaymentInfo,
      repaymentDate,
      storageFeeLate,
      allTransactions,
      emitTransactions,
      overdueFee,
      liquidizationFee,
      failedCommitmentFee
    };
  }
};
</script>

<style lang="scss">
@import "~@/assets/scss/colors";

#contract-editor {
  padding-top: 50px;
  padding-bottom: 50px;

  h1 {
    margin-bottom: 20px;
    font-size: 28px;
    margin-left: 20px;
  }

  .box {
    background-color: $white;
    border-radius: 4px;
    margin: 15px;
    padding: 20px 0;
    padding-bottom: 40px;
    max-width: 1200px;

    h2 {
      font-size: 20px;
      font-weight: bold;
      margin-bottom: 30px;
      margin-top: 20px;
    }

    .input-wrapper {
      > .input {
        width: 100%;
        margin-bottom: 20px;
      }
    }
  }

  .controls {
    text-align: right;
    margin-top: 20px;
    max-width: 1200px;

    .primary-button {
      margin-left: 15px;
      border-radius: 4px;
    }
  };

  .preview-controls {
    background-color: $white;
    font-size: 20px;
    padding: 30px;
    position: fixed;
    top: 200px;
    right: 15px;
    width: 350px;
    border-radius: 4px;

    @media (max-width: 1439px) {
      opacity: 0.5;
      transition: all .2s ease-in-out;

      &:hover {
        opacity: 1;
      }
    }

    center {
      font-weight: bolder;
      text-transform: uppercase;
    }

    .buttons {
      margin-top: 28px;

      .button {
        margin: 0px 4px;
        box-shadow: 0px 3px 6px $silver;
      }

      .primary-button {
        padding: 0px 20px;

        /deep/ span {
          font-size: 15px;
        }
      }
    }
  }
}
</style>
